/**
 * A sheet core's promotion state (`SheetHandleCore`): whether the sheet is
 * still local (opened ephemeral, perhaps from a template) or synced, the
 * promote routine the client installed for it, the one attempt in flight,
 * and the in-place re-key that ends it. The core hands it a narrow host
 * (`CorePromotion.Host`): the sheet as it stands, the client's row ids, and
 * the three writes a re-key makes (re-key the rows, adopt the server's entry,
 * persist the locally held cells). A promote routine reaches the core only
 * through the seam built here (`CorePromotion.Promotable`).
 */
import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import type { Rational } from "@tento-core/rational";
import { RowKey, type RowLocalId, SheetId } from "@tento-lona/sheets";
import { NotPromotableError } from "../errors.ts";
import type { Network } from "../network.ts";
import type { PromotionCommit } from "./promotion-commit.ts";

export namespace CorePromotion {
  /** How the sheet was opened: stored on the server, or local until promoted (ephemeral, or from a template). */
  export type Mode = "persisted" | "ephemeral" | "template";

  /**
   * What a promote routine reaches of the core it promotes
   * (`SheetHandleCore.installPromoter`): the sheet as it stands, the
   * promote's create step (each row's position) and re-key step, and
   * `promoted`, once the sheet is on the server.
   */
  export interface Promotable {
    /** The sheet's metadata (its id is the local, client-minted one until the re-key). */
    readonly network: Network.UserSheet;
    readonly id: SheetId;
    readonly title: Option<string>;
    /** Bumped by structural changes only: a promote must not move it. */
    readonly generation: number;
    /** The sheet and its rows as the server should store them (synthetic rows excluded). */
    serialize(): Network.UserSheetWithRows;
    /** The promote's create step: each row's position, by its raw id. */
    rowPositions(): Map<string, Rational>;
    /** The promote's in-place re-key, after the server stored the sheet. */
    applyPromotion(serverData: Network.UserSheetWithRows, idRemap: ReadonlyMap<string, string>): Promise<PromotionCommit>;
    /** The sheet is on the server: clear the promote routine. */
    promoted(): void;
  }

  /** What the promotion reaches of its core. */
  export interface Host {
    readonly network: () => Network.UserSheet;
    readonly id: () => SheetId;
    readonly title: () => Option<string>;
    readonly generation: () => number;
    readonly serialize: () => Network.UserSheetWithRows;
    readonly rowPositions: () => Map<string, Rational>;
    /** The client's row ids: a re-keyed row's raw id resolves through them. */
    readonly ids: Pick<RowLocalId.Resolver, "resolve">;
    /** Re-key the core's rows, cell caches and row preferences in place (one mirrored write). */
    readonly rekey: (idRemap: ReadonlyMap<string, string>) => void;
    /** Adopt the server's entry for the sheet (its rows are the core's own, re-keyed). */
    readonly adopt: (sheet: Network.UserSheet) => void;
    /** Persist the cells the sheet held locally while it was ephemeral. */
    readonly flushCells: () => Promise<void>;
  }
}

/** One core's promotion: see the module comment. */
export class CorePromotion {
  readonly #host: CorePromotion.Host;
  /** Local until a promote's re-key; `false` for a sheet opened as stored. */
  #local: boolean;
  /**
   * Opened from a template (the app passes it through
   * `openEphemeral(spec, { mode: "template", … })`). Only affects the
   * ephemeral banner copy ("Use this template" vs the generic "save to your
   * sheets"); irrelevant once promoted, since the banner stops showing.
   */
  readonly #template: boolean;
  /**
   * The promote routine `LonaSdk.Client` installs on an ephemeral core
   * (`install`); `null` once promoted or for a sheet that cannot be promoted.
   */
  #promoter: Option<() => Promise<void>> = null;
  /**
   * The attempt in flight, shared so concurrent mutations trigger exactly one
   * promote (the rest await it). Cleared on failure so a later mutation
   * retries; irrelevant after success (persistence is `"synced"`).
   */
  #attempt: Option<Promise<void>> = null;

  constructor(host: CorePromotion.Host, mode: CorePromotion.Mode) {
    this.#host = host;
    this.#local = mode !== "persisted";
    this.#template = mode === "template";
  }

  /** `"local"` until the sheet is promoted (or for a sheet opened ephemeral); `"synced"` after. */
  get persistence(): "local" | "synced" {
    return this.#local ? "local" : "synced";
  }

  /** `true` when this ephemeral sheet was opened from a template. */
  get isTemplate(): boolean {
    return this.#template;
  }

  /** The mode the sheet is in now (a promoted ephemeral sheet is `"persisted"`). */
  get mode(): CorePromotion.Mode {
    return !this.#local ? "persisted" : this.#template ? "template" : "ephemeral";
  }

  /**
   * Install the promote routine `build` makes from this core's seam
   * (`CorePromotion.Promotable`), built here and handed only to `build`.
   */
  install(build: (sheet: CorePromotion.Promotable) => () => Promise<void>): void {
    const host = this.#host;
    const sheet: CorePromotion.Promotable = Object.freeze({
      get network(): Network.UserSheet {
        return host.network();
      },
      get id(): SheetId {
        return host.id();
      },
      get title(): Option<string> {
        return host.title();
      },
      get generation(): number {
        return host.generation();
      },
      serialize: () => host.serialize(),
      rowPositions: () => host.rowPositions(),
      applyPromotion: (serverData: Network.UserSheetWithRows, idRemap: ReadonlyMap<string, string>) =>
        this.#apply(serverData, idRemap),
      promoted: () => {
        this.#promoter = null;
      },
    });
    this.#promoter = build(sheet);
  }

  /**
   * Promote the sheet if it is still local: one attempt, shared by every
   * caller while it runs. Resolves at once for a synced sheet; rejects with
   * `NotPromotableError` for a local sheet with no promote routine.
   */
  ensure(): Promise<void> {
    if (this.persistence !== "local") return Promise.resolve();
    if (!this.#promoter) {
      return Promise.reject(
        new NotPromotableError(this.#host.id(), this.persistence),
      );
    }
    if (!this.#attempt) {
      this.#attempt = this.#promoter().catch((e) => {
        this.#attempt = null;
        throw e;
      });
    }
    return this.#attempt;
  }

  /**
   * The in-place re-key the client's promote routine runs once the server
   * has created the sheet (see `LonaSdk.Client.openEphemeral`).
   *
   * Returns the `PromotionCommit` describing what it did — the typed
   * replacement for a host's `actor.name === "sheet-promote"` string check.
   * Every entry's `from` is captured BEFORE the re-key and its `to` AFTER, so
   * a healthy promote yields `from === to` for every row (the transparency
   * claim, assertable) and a missed re-key shows up as a divergence rather
   * than as a silently blank sheet.
   */
  async #apply(
    serverData: Network.UserSheetWithRows,
    idRemap: ReadonlyMap<string, string>,
  ): Promise<PromotionCommit> {
    const host = this.#host;
    this.#local = false;
    // Capture each row's PRE-re-key identity. `resolve` is idempotent for keys
    // the ephemeral tree already interned, which is every key in the remap.
    const remapBefore: { readonly key: string; readonly from: RowLocalId }[] = [];
    for (const [fromRaw, toRaw] of idRemap) {
      remapBefore.push({ key: toRaw, from: host.ids.resolve(RowKey.of(fromRaw)) });
    }
    // In place, not a rebuild: no generation bump, so nothing re-renders
    // (see the core's `#rekeyInPlace`).
    const ephemeralId = host.id().toString();
    const genBefore = host.generation();
    host.rekey(idRemap);
    // Refresh the sheet METADATA to the server's authoritative copy (the id was
    // honored, so this is title / role / timestamps). Strip `rows` and swap only
    // the metadata — not a tree rebuild — so it triggers no render.
    const { rows: _promotedRows, ...sheetMeta } = serverData;
    host.adopt(sheetMeta);
    // Only this synchronous transaction belongs to promotion. Provider/alias
    // materialization during network or cell-flush awaits remains legitimate.
    if (host.generation() !== genBefore) {
      Tracing.log("cow-promote-rerender", {
        ephemeralId,
        genBefore,
        genAfter: host.generation(),
      });
    }
    // Promote carries ROWS, not cells — flush the rows-side sheet's source cells
    // (the template's seed cells + any pre-promote edit that triggered the claim)
    // to the now-synced backend so they survive a reload.
    await host.flushCells();
    // Resolve the POST-re-key identity of each server key. After a successful
    // re-key the server key is aliased onto the surviving local id, so this
    // returns the same object `from` holds; if the re-key missed the row it
    // mints a fresh one, which is exactly the divergence worth reporting.
    return {
      kind: "transparent-rekey",
      sheetId: SheetId.of(host.network().id.toString()),
      rowRemap: remapBefore.map((entry) => ({
        from: entry.from,
        to: host.ids.resolve(RowKey.of(entry.key)),
      })),
    };
  }
}
